import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { NewInput } from '../../PIM/common';
import { roundOff } from '../../helper';
import useDeferredRowChange from '../useDeferredRowChange';
import type { BombiixCellRendererParams, BombiixRow, BombiixRowId } from '../types';

interface PercentInputCellExtras<TRow extends BombiixRow = BombiixRow> {
  /**
   * Resolves the derived amount rendered in brackets beside the rate, e.g. the `(£ 4.2)` a 10%
   * duty produces. Omit it — or return nullish — to render the rate on its own, which is what a
   * pure ratio column such as Margin Per Unit needs.
   */
  calcAccessor?: (row: TRow) => number | string | null | undefined;
  /** Currency symbol for the bracketed amount. Falls back to the cell's own `currencySymbol`. */
  calcCurrencySymbol?: string;
  /** Decimal places for the bracketed amount. */
  calcPrecision?: number;
  /**
   * Class applied to the field only while a bracketed amount sits beside it, so the two share the
   * cell width. Caller-controlled; ignored entirely when no bracket is rendered.
   */
  pairedFieldClassName?: string;
}

type PercentInputCellProps<TRow extends BombiixRow = BombiixRow> = BombiixCellRendererParams<TRow, string> & PercentInputCellExtras<TRow>;

/**
 * Editable percentage cell: the rate the user types, a `%` suffix inside the field, and the
 * derived amount the rate produces alongside it.
 *
 * Mirrors CurrencyInputCell with the affix on the trailing edge, and matches the landed
 * information table on the costing detail page so the same figure reads identically in both
 * places. Like every affix cell here the input owns its display value locally, so a keystroke
 * never waits on the parent's recalc pipeline and the value is never re-set under the caret;
 * external values reflow only while the cell is unfocused.
 */
function PercentInputCell<TRow extends BombiixRow = BombiixRow>(params: PercentInputCellProps<TRow>) {
  const {
    value,
    data,
    colDef,
    onRowChange,
    getRowId,
    currencySymbol = '£',
    placeholder,
    disabled,
    readOnly,
    error,
    calcAccessor,
    calcCurrencySymbol,
    calcPrecision = 2,
    pairedFieldClassName = 'w-80px flex-shrink-0',
  } = params;
  const rowId: BombiixRowId | undefined = getRowId ? getRowId(data as TRow) : (data as { id?: BombiixRowId } | undefined)?.id;
  const field = colDef?.field;

  const focusedRef = useRef<boolean>(false);
  const [local, setLocal] = useState<string>(value ?? '');
  const [commitChange, flushChange] = useDeferredRowChange(onRowChange);

  useEffect(() => {
    if (!focusedRef.current) setLocal(value ?? '');
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/[^0-9.\-]/g, '');
    setLocal(cleaned);
    if (rowId !== undefined && field) commitChange(rowId, field, cleaned);
  };

  const calculated = typeof calcAccessor === 'function' && data ? calcAccessor(data as TRow) : undefined;
  const hasCalculated = calculated !== undefined && calculated !== null && calculated !== '';

  return (
    <div className={`cag-cell-wrap${error ? ' cag-cell-wrap--error' : ''}`}>
      <div className="cag-cell">
        <div className={`cag-affix-field${hasCalculated ? ` ${pairedFieldClassName}` : ''}${disabled || readOnly ? ' cag-affix-field-disabled' : ''}`}>
          <NewInput
            className="cag-affix-input"
            type="text"
            inputMode="decimal"
            invalid={Boolean(error)}
            value={local}
            placeholder={placeholder ?? colDef?.headerName ?? 'Text Box'}
            disabled={disabled}
            readOnly={readOnly}
            onFocus={() => {
              focusedRef.current = true;
            }}
            onBlur={() => {
              focusedRef.current = false;
              flushChange();
              setLocal(value ?? '');
            }}
            onChange={handleChange}
          />
          <span className="cag-affix cag-affix-suffix">%</span>
        </div>
        {hasCalculated ? <span className="text-nowrap ml-1">{`(${calcCurrencySymbol ?? currencySymbol} ${roundOff(calculated ?? 0, calcPrecision)})`}</span> : null}
      </div>
      {error ? <span className="cag-cell-error">{error}</span> : null}
    </div>
  );
}

export default PercentInputCell;
